import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { AdminService, BaseComponent, UserService } from "@mypxplat/xplat/core";
import { Chart, ChartConfiguration, registerables } from "chart.js";
import { saveAs } from "file-saver";

Chart.register(...registerables);

interface UsageMonth {
  month: string;
  unique_users: number;
  messages: number;
  user_days: number;
  avg_messages_per_user: number;
  median_messages_per_user: number;
  new_users: number;
  returning_users: number;
}

interface UsageReport {
  months: UsageMonth[];
  history_starts: string | null;
  generated_at: string;
}

/** One table row, fully precomputed so the template contains no method calls. */
interface UsageRow extends UsageMonth {
  label: string;
  returningShare: string;
  /** True for months at the very start of retained history, where new-vs-returning understates returning. */
  beforeFullHistory: boolean;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-ai-assistant-usage",
  templateUrl: "ai-assistant-usage.component.html",
  styleUrls: ["ai-assistant-usage.component.scss"],
  standalone: false,
})
export class AiAssistantUsageComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild("usageChart") usageChartRef: ElementRef<HTMLCanvasElement>;

  public loading: boolean = true;
  public loadError: boolean = false;
  public rows: UsageRow[] = [];
  public historyStarts: string = "";
  public generatedAt: string = "";
  public showCustomRange: boolean = false;
  public startMonth: string = "";
  public endMonth: string = "";
  public rangeError: string = "";

  private chart: Chart<"line"> | null = null;

  constructor(
    userService: UserService,
    public adminService: AdminService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.load();
  }

  override ngOnDestroy() {
    this.destroyChart();
    super.ngOnDestroy();
  }

  load() {
    // An empty bound means "let the API decide", which yields the trailing 12 months.
    if (this.startMonth && this.endMonth && this.startMonth > this.endMonth) {
      this.rangeError = "The start month must not be after the end month.";
      return;
    }
    this.rangeError = "";
    this.loading = true;
    this.loadError = false;
    this.destroyChart();

    this.adminService.getAiAssistantUsage(this.startMonth || undefined, this.endMonth || undefined).subscribe({
      next: (result: UsageReport) => {
        this.historyStarts = result?.history_starts || "";
        this.generatedAt = result?.generated_at || "";
        this.rows = this.buildRows(result?.months || []);
        this.loading = false;
        setTimeout(() => this.createChart());
      },
      error: () => {
        this.rows = [];
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  resetRange() {
    this.startMonth = "";
    this.endMonth = "";
    this.load();
  }

  /**
   * Newest month first — the question people open this screen with is almost always
   * about the month that just closed.
   */
  private buildRows(months: UsageMonth[]): UsageRow[] {
    const horizonMonth = this.historyStarts ? this.historyStarts.slice(0, 7) : "";
    return [...months]
      .sort((a, b) => (a.month < b.month ? 1 : -1))
      .map((month) => ({
        ...month,
        label: this.formatMonth(month.month),
        returningShare: month.unique_users ? `${Math.round((month.returning_users / month.unique_users) * 100)}%` : "—",
        beforeFullHistory: !!horizonMonth && month.month <= horizonMonth,
      }));
  }

  private formatMonth(month: string): string {
    const [year, monthNumber] = month.split("-");
    return `${MONTH_LABELS[Number(monthNumber) - 1] || month} ${year}`;
  }

  private destroyChart() {
    this.chart?.destroy();
    this.chart = null;
  }

  private createChart() {
    if (!this.usageChartRef?.nativeElement || !this.rows.length) return;
    const ctx = this.usageChartRef.nativeElement.getContext("2d");
    if (!ctx) return;

    // Chronological for the chart even though the table reads newest-first.
    const ordered = [...this.rows].reverse();

    // Messages run roughly an order of magnitude above unique users, so they get
    // their own axis rather than flattening the user line against the baseline.
    const config: ChartConfiguration<"line"> = {
      type: "line",
      data: {
        labels: ordered.map((row) => row.label),
        datasets: [
          {
            label: "Messages",
            data: ordered.map((row) => row.messages),
            borderColor: "rgb(213, 43, 30)",
            backgroundColor: "rgb(213, 43, 30)",
            yAxisID: "y",
            tension: 0.2,
            fill: false,
            borderWidth: 2,
            pointRadius: 3,
          },
          {
            label: "Unique users",
            data: ordered.map((row) => row.unique_users),
            borderColor: "rgb(54, 162, 235)",
            backgroundColor: "rgb(54, 162, 235)",
            yAxisID: "y1",
            tension: 0.2,
            fill: false,
            borderWidth: 2,
            pointRadius: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: "AI Assistant usage by month" },
          legend: { position: "top" },
        },
        interaction: { mode: "index", intersect: false },
        scales: {
          y: { beginAtZero: true, position: "left", title: { display: true, text: "Messages" } },
          y1: { beginAtZero: true, position: "right", title: { display: true, text: "Unique users" }, grid: { drawOnChartArea: false } },
          x: { title: { display: true, text: "Month" } },
        },
      },
    };

    this.chart = new Chart(ctx, config);
  }

  // Escapes a single CSV cell value.
  private _csvCell(value: any): string {
    const str = value == null ? "" : String(value);
    return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  }

  // Builds a CSV: a title line, the caveat notice, a blank row, the header, then the rows.
  private _buildCsv(title: string, headers: string[], rows: any[][]): string {
    const lines = [this._csvCell(title), this._csvCell(this.csvNotice), ""];
    lines.push(headers.map((cell) => this._csvCell(cell)).join(","));
    rows.forEach((row) => lines.push(row.map((cell) => this._csvCell(cell)).join(",")));
    return lines.join("\r\n");
  }

  get csvNotice(): string {
    const horizon = this.historyStarts ? ` Usage history begins ${this.historyStarts}, so "New users" understates returning users in the earliest month.` : "";
    return `Messages counts completed exchanges (one prompt plus its reply). Months are UTC.${horizon}`;
  }

  downloadCsv() {
    const rows = [...this.rows]
      .reverse()
      .map((row) => [row.month, row.unique_users, row.messages, row.user_days, row.avg_messages_per_user, row.median_messages_per_user, row.new_users, row.returning_users]);

    const csv = this._buildCsv(
      "AI Assistant Usage by Month",
      ["Month", "Unique Users", "Messages (exchanges)", "User Days", "Avg Messages/User", "Median Messages/User", "New Users", "Returning Users"],
      rows
    );
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "ai-assistant-usage-by-month.csv");
  }
}
